import { apiFetch } from '../../auth/AuthContext';
/**
 * CronHealthTab — live health status of every scheduled cron job.
 * Features: auto-refresh, manual refresh, 24h timeline, run history dots,
 * unhealthy-since, next 3 upcoming, search filter, log download.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface CronJob {
  name: string;
  description: string;
  schedule: string;
  expected_interval_s: number;
  scheduled_in_crontab: boolean;
  log_path: string | null;
  last_run_ts: string | null;
  last_run_age_s: number | null;
  last_run_human: string;
  status: 'healthy' | 'stale' | 'error' | 'unknown';
  recent_lines: string[];
}

interface CronStatus {
  checked_at: string;
  overall_status: 'healthy' | 'stale' | 'error' | 'unknown';
  jobs: CronJob[];
}

const STATUS_COLORS = {
  healthy: '#3fb950',
  stale: '#d29922',
  error: '#f85149',
  unknown: '#8b949e',
} as const;

const STATUS_LABELS = {
  healthy: 'Healthy',
  stale: 'Stale',
  error: 'Overdue',
  unknown: 'No data',
} as const;

const REFRESH_INTERVAL = 30;

// ── helpers ──────────────────────────────────────────────────────────────────

function humanizeCron(schedule: string): string {
  const parts = schedule.split(/\s+/);
  if (parts.length !== 5) return schedule;
  const [min, hour, dom, _mon, dow] = parts;
  if (schedule === '*/5 * * * *') return 'Every 5 min';
  if (schedule === '0 * * * *') return 'Every hour';
  if (dom === '*' && dow === '*' && min === '0')
    return `Daily ${hour.padStart(2, '0')}:00 UTC`;
  if (dow !== '*' && dow !== '0-6') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `Weekly ${days[parseInt(dow)] ?? dow} ${hour.padStart(2, '0')}:${min.padStart(2, '0')} UTC`;
  }
  return schedule;
}

function nextCronRun(schedule: string): Date | null {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minPart, hourPart, , , dowPart] = parts;
  const now = new Date();

  if (minPart.startsWith('*/')) {
    const n = parseInt(minPart.slice(2));
    const minsUntil = n - (now.getUTCMinutes() % n);
    const d = new Date(now);
    d.setUTCMinutes(now.getUTCMinutes() + minsUntil, 0, 0);
    return d;
  }
  const min = parseInt(minPart);
  if (hourPart === '*') {
    const d = new Date(now);
    d.setUTCMinutes(min, 0, 0);
    if (d <= now) d.setUTCHours(d.getUTCHours() + 1);
    return d;
  }
  const hour = parseInt(hourPart);
  if (dowPart !== '*') {
    const targetDow = parseInt(dowPart);
    let d = new Date(now);
    d.setUTCHours(hour, min, 0, 0);
    for (let i = 0; i < 8; i++) {
      if (d.getUTCDay() === targetDow && d > now) return d;
      d = new Date(d.getTime() + 86_400_000);
      d.setUTCHours(hour, min, 0, 0);
    }
    return null;
  }
  const d = new Date(now);
  d.setUTCHours(hour, min, 0, 0);
  if (d <= now) d.setUTCDate(d.getUTCDate() + 1);
  return d;
}

function timeUntil(d: Date): string {
  const s = Math.floor((d.getTime() - Date.now()) / 1000);
  if (s < 0) return 'overdue';
  if (s < 60) return `in ${s}s`;
  if (s < 3600) return `in ${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400)
    return `in ${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `in ${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

function formatAge(s: number): string {
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400)
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

function unhealthySince(job: CronJob): string | null {
  if (job.status !== 'stale' && job.status !== 'error') return null;
  if (job.last_run_age_s == null) return null;
  const forS = job.last_run_age_s - job.expected_interval_s * 1.5;
  if (forS <= 0) return null;
  return formatAge(forS);
}

function logLineColor(line: string): string {
  const u = line.toUpperCase();
  if (/ERROR|FAIL|CRITICAL|EXCEPTION|TRACEBACK/.test(u)) return '#f85149';
  if (/WARN/.test(u)) return '#d29922';
  if (/SUCCESS|DONE|COMPLETE|FINISH/.test(u)) return '#3fb950';
  return '#c9d1d9';
}

// Returns schedule positions as fraction of 24h (0–1), or isBand for high-frequency
function schedulePositions(schedule: string): {
  positions: number[];
  isBand: boolean;
} {
  const parts = schedule.split(/\s+/);
  if (parts.length !== 5) return { positions: [], isBand: false };
  const [min, hour] = parts;
  if (min.startsWith('*/')) return { positions: [], isBand: true };
  const m = parseInt(min);
  if (hour === '*') {
    return {
      positions: Array.from({ length: 24 }, (_, h) => (h * 60 + m) / 1440),
      isBand: false,
    };
  }
  const h = parseInt(hour);
  return { positions: [(h * 60 + m) / 1440], isBand: false };
}

// ── sub-components ────────────────────────────────────────────────────────────

function HistoryDots({ statuses }: { statuses: string[] }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 3 }}
      title="Last runs (oldest→newest)"
    >
      {Array.from({ length: 7 }, (_, i) => {
        const idx = statuses.length - 7 + i;
        const s = idx >= 0 ? statuses[idx] : null;
        const color = s
          ? STATUS_COLORS[s as keyof typeof STATUS_COLORS]
          : '#21262d';
        return (
          <span
            key={i}
            title={s ?? 'no data'}
            style={{
              display: 'inline-block',
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: color,
              border: s ? 'none' : '1px solid #30363d',
            }}
          />
        );
      })}
    </div>
  );
}

function ScheduleTimeline({ jobs }: { jobs: CronJob[] }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const now = new Date();
  const nowFrac = (now.getUTCHours() * 60 + now.getUTCMinutes()) / 1440;
  const bandJobs = jobs.filter((j) => schedulePositions(j.schedule).isBand);
  const pointJobs = jobs.filter((j) => !schedulePositions(j.schedule).isBand);

  return (
    <div
      style={{
        background: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 10,
        padding: '14px 20px',
        marginBottom: 16,
      }}
    >
      <div
        style={{
          color: '#8b949e',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        24h Schedule (UTC) — current time{' '}
        {now.getUTCHours().toString().padStart(2, '0')}:
        {now.getUTCMinutes().toString().padStart(2, '0')}
      </div>

      <div style={{ position: 'relative' }}>
        {/* Current time line — spans all rows */}
        <div
          style={{
            position: 'absolute',
            left: `calc(112px + ${nowFrac * 100}% * (100% - 112px) / 100%)`,
            top: 0,
            bottom: 20,
            width: 1,
            background: '#3fb95055',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />

        {/* Point jobs */}
        {pointJobs.map((job) => {
          const color = STATUS_COLORS[job.status];
          const { positions } = schedulePositions(job.schedule);
          return (
            <div
              key={job.name}
              style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}
            >
              <div
                style={{
                  width: 100,
                  flexShrink: 0,
                  color: '#484f58',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  textAlign: 'right',
                  paddingRight: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {job.name}
              </div>
              <div style={{ flex: 1, position: 'relative', height: 14 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 6,
                    height: 2,
                    background: '#21262d',
                    borderRadius: 1,
                  }}
                />
                {/* Current-time tick on this row */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${nowFrac * 100}%`,
                    top: 2,
                    width: 1,
                    height: 10,
                    background: '#3fb95055',
                    pointerEvents: 'none',
                  }}
                />
                {positions.slice(0, 24).map((pos, pi) => (
                  <div
                    key={pi}
                    title={`${job.name}: ${Math.floor(pos * 24)
                      .toString()
                      .padStart(
                        2,
                        '0',
                      )}:${String(Math.floor(((pos * 24) % 1) * 60)).padStart(2, '0')} UTC`}
                    style={{
                      position: 'absolute',
                      left: `${pos * 100}%`,
                      top: '50%',
                      width: 8,
                      height: 8,
                      transform: 'translate(-50%, -50%)',
                      borderRadius: '50%',
                      background: color,
                      boxShadow: `0 0 4px ${color}66`,
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Band jobs */}
        {bandJobs.map((job) => {
          const color = STATUS_COLORS[job.status];
          return (
            <div
              key={job.name}
              style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}
            >
              <div
                style={{
                  width: 100,
                  flexShrink: 0,
                  color: '#484f58',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  textAlign: 'right',
                  paddingRight: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {job.name}
              </div>
              <div style={{ flex: 1, position: 'relative', height: 14 }}>
                <div
                  title={`${job.name} (${job.schedule})`}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 4,
                    height: 6,
                    borderRadius: 2,
                    background: `repeating-linear-gradient(90deg, ${color}44 0px, ${color}44 2px, transparent 2px, transparent 5px)`,
                    border: `1px solid ${color}22`,
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Hour labels */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 100, flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative', height: 14 }}>
            {[0, 6, 12, 18, 24].map((h) => (
              <span
                key={h}
                style={{
                  position: 'absolute',
                  left: `${(h / 24) * 100}%`,
                  transform: 'translateX(-50%)',
                  color: '#484f58',
                  fontSize: 9,
                  fontFamily: 'monospace',
                }}
              >
                {h.toString().padStart(2, '0')}h
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          color: '#8b949e',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ color: '#c9d1d9', fontSize: 12 }}>{value}</div>
    </div>
  );
}

function formatInterval(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} hours`;
  if (s < 604800) return `${Math.round(s / 86400)} days`;
  return `${Math.round(s / 604800)} weeks`;
}

// ── main component ────────────────────────────────────────────────────────────

export default function CronHealthTab() {
  const [data, setData] = useState<CronStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [statusRes, histRes] = await Promise.all([
        apiFetch('/data/cron/status'),
        apiFetch('/data/cron/history'),
      ]);
      if (!statusRes.ok) throw new Error(`HTTP ${statusRes.status}`);
      setData(await statusRes.json());
      if (histRes.ok) setHistory(await histRes.json());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          load();
          return REFRESH_INTERVAL;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [load]);

  const handleManualRefresh = () => {
    load();
    setCountdown(REFRESH_INTERVAL);
  };

  const handleCopyPath = (path: string) => {
    navigator.clipboard.writeText(path).then(() => {
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    });
  };

  const toggleExpand = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (loading && !data)
    return (
      <div style={{ color: '#8b949e', textAlign: 'center', padding: 60 }}>
        Loading cron status...
      </div>
    );
  if (error && !data)
    return (
      <div style={{ color: '#f85149', textAlign: 'center', padding: 60 }}>
        Error: {error}
      </div>
    );
  if (!data) return null;

  const healthyCount = data.jobs.filter((j) => j.status === 'healthy').length;
  const staleCount = data.jobs.filter((j) => j.status === 'stale').length;
  const errorCount = data.jobs.filter((j) => j.status === 'error').length;
  const unknownCount = data.jobs.filter((j) => j.status === 'unknown').length;
  const overallColor = STATUS_COLORS[data.overall_status];

  const filteredJobs = data.jobs.filter(
    (j) =>
      !search ||
      j.name.toLowerCase().includes(search.toLowerCase()) ||
      j.description.toLowerCase().includes(search.toLowerCase()),
  );

  // Next 3 upcoming runs sorted by fire time
  const upcoming = data.jobs
    .map((j) => ({ job: j, next: nextCronRun(j.schedule) }))
    .filter((x) => x.next !== null)
    .sort((a, b) => a.next!.getTime() - b.next!.getTime())
    .slice(0, 3);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <h2 style={{ color: '#c9d1d9', fontWeight: 600, margin: '0 0 6px' }}>
            Cron Health
          </h2>
          <p style={{ color: '#8b949e', fontSize: 13, margin: 0 }}>
            Live status of scheduled jobs based on log file freshness.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{ color: '#484f58', fontSize: 11, fontFamily: 'monospace' }}
          >
            refresh in {countdown}s
          </span>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            style={{
              background: '#161b22',
              border: '1px solid #30363d',
              borderRadius: 6,
              color: loading ? '#484f58' : '#8b949e',
              fontSize: 11,
              padding: '4px 10px',
              cursor: loading ? 'default' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            ↻ Refresh
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 16px',
              background: '#0d1117',
              border: `1px solid ${overallColor}44`,
              borderRadius: 20,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: overallColor,
                boxShadow: `0 0 8px ${overallColor}88`,
              }}
            />
            <span
              style={{ color: overallColor, fontSize: 13, fontWeight: 600 }}
            >
              {STATUS_LABELS[data.overall_status]}
            </span>
            <span
              style={{
                color: '#484f58',
                fontSize: 11,
                fontFamily: 'monospace',
              }}
            >
              {new Date(data.checked_at).toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          {
            label: 'Healthy',
            count: healthyCount,
            color: STATUS_COLORS.healthy,
          },
          { label: 'Stale', count: staleCount, color: STATUS_COLORS.stale },
          { label: 'Overdue', count: errorCount, color: STATUS_COLORS.error },
          {
            label: 'No data',
            count: unknownCount,
            color: STATUS_COLORS.unknown,
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: '#161b22',
              border: `1px solid ${s.color}33`,
              borderRadius: 10,
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: s.color,
                flexShrink: 0,
              }}
            />
            <div>
              <div
                style={{
                  color: '#8b949e',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}
              >
                {s.label}
              </div>
              <div
                style={{
                  color: '#e6edf3',
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {s.count}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 24h schedule timeline */}
      <ScheduleTimeline jobs={data.jobs} />

      {/* Next 3 upcoming + search */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        {/* Next 3 upcoming */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              color: '#484f58',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Next up:
          </span>
          {upcoming.map(({ job, next }) => (
            <span
              key={job.name}
              style={{
                background: '#161b22',
                border: `1px solid ${STATUS_COLORS[job.status]}44`,
                borderRadius: 6,
                padding: '3px 10px',
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#c9d1d9',
              }}
            >
              <span style={{ color: STATUS_COLORS[job.status] }}>●</span>{' '}
              {job.name}{' '}
              <span style={{ color: '#484f58' }}>{timeUntil(next!)}</span>
            </span>
          ))}
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Filter jobs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            background: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 6,
            color: '#c9d1d9',
            fontSize: 12,
            padding: '5px 12px',
            outline: 'none',
            fontFamily: 'inherit',
            width: 180,
          }}
        />
      </div>

      {/* Job list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filteredJobs.map((job) => {
          const color = STATUS_COLORS[job.status];
          const isExpanded = expanded.has(job.name);
          const nextRun = nextCronRun(job.schedule);
          const badSince = unhealthySince(job);
          const jobHistory = history[job.name] ?? [];

          return (
            <div
              key={job.name}
              style={{
                background: '#161b22',
                border: `1px solid ${isExpanded ? color + '66' : '#30363d'}`,
                borderRadius: 10,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
            >
              {/* Header row */}
              <button
                onClick={() => toggleExpand(job.name)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '16px 20px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                {/* Status dot */}
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: color,
                    boxShadow: `0 0 8px ${color}66`,
                    flexShrink: 0,
                  }}
                />

                {/* Name + description + unhealthy since */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 2,
                    }}
                  >
                    <code
                      style={{
                        color: '#e6edf3',
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: 'monospace',
                      }}
                    >
                      {job.name}
                    </code>
                    {!job.scheduled_in_crontab && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#f85149',
                          background: '#f8514922',
                          padding: '1px 6px',
                          borderRadius: 4,
                          border: '1px solid #f8514944',
                        }}
                      >
                        NOT IN CRONTAB
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#8b949e', fontSize: 12 }}>
                    {job.description}
                  </div>
                  {badSince && (
                    <div style={{ color: color, fontSize: 10, marginTop: 2 }}>
                      {job.status === 'error' ? 'Overdue' : 'Stale'} for{' '}
                      {badSince}
                    </div>
                  )}
                </div>

                {/* History dots */}
                <div style={{ flexShrink: 0 }}>
                  <HistoryDots statuses={jobHistory} />
                </div>

                {/* Schedule + next run */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div
                    style={{
                      color: '#8b949e',
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                    }}
                  >
                    Schedule
                  </div>
                  <div
                    style={{
                      color: '#c9d1d9',
                      fontSize: 12,
                      fontFamily: 'monospace',
                    }}
                  >
                    {humanizeCron(job.schedule)}
                  </div>
                  {nextRun && (
                    <div
                      style={{
                        color: '#484f58',
                        fontSize: 11,
                        fontFamily: 'monospace',
                        marginTop: 2,
                      }}
                    >
                      {timeUntil(nextRun)}
                    </div>
                  )}
                </div>

                {/* Last run */}
                <div
                  style={{ textAlign: 'right', flexShrink: 0, minWidth: 110 }}
                >
                  <div
                    style={{
                      color: '#8b949e',
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                    }}
                  >
                    Last run
                  </div>
                  <div
                    style={{
                      color,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'monospace',
                    }}
                  >
                    {job.last_run_human}
                  </div>
                </div>

                {/* Status badge */}
                <div
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    background: color + '22',
                    color,
                    border: `1px solid ${color}66`,
                    minWidth: 70,
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {STATUS_LABELS[job.status]}
                </div>

                <span
                  style={{
                    color: '#484f58',
                    fontSize: 13,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div
                  style={{
                    padding: '0 20px 16px',
                    borderTop: '1px solid #21262d',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 14,
                      padding: '14px 0',
                    }}
                  >
                    <DetailItem
                      label="Cron expression"
                      value={
                        <code style={{ color: '#a5d6ff', fontSize: 12 }}>
                          {job.schedule}
                        </code>
                      }
                    />
                    <DetailItem
                      label="Expected interval"
                      value={
                        <span style={{ color: '#c9d1d9' }}>
                          {formatInterval(job.expected_interval_s)}
                        </span>
                      }
                    />
                    <DetailItem
                      label="Last run"
                      value={
                        <span
                          style={{
                            color: '#c9d1d9',
                            fontFamily: 'monospace',
                            fontSize: 11,
                          }}
                        >
                          {job.last_run_ts
                            ? new Date(job.last_run_ts).toLocaleString()
                            : '—'}
                        </span>
                      }
                    />
                    <DetailItem
                      label="Log file"
                      value={
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <code
                            style={{
                              color: '#8b949e',
                              fontSize: 11,
                              wordBreak: 'break-all',
                            }}
                          >
                            {job.log_path || '—'}
                          </code>
                          {job.log_path && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyPath(job.log_path!);
                              }}
                              title="Copy path"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                flexShrink: 0,
                                color:
                                  copiedPath === job.log_path
                                    ? '#3fb950'
                                    : '#484f58',
                                fontSize: 12,
                                lineHeight: 1,
                              }}
                            >
                              {copiedPath === job.log_path ? '✓' : '⎘'}
                            </button>
                          )}
                        </div>
                      }
                    />
                  </div>

                  {/* Log download button */}
                  <div style={{ marginBottom: 10 }}>
                    <a
                      href={`/data/cron/log/${job.name}`}
                      download={`${job.name}.log`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 6,
                        color: '#8b949e',
                        fontSize: 11,
                        padding: '4px 12px',
                        textDecoration: 'none',
                        fontFamily: 'inherit',
                      }}
                    >
                      ↓ Download full log
                    </a>
                  </div>

                  {job.recent_lines.length > 0 && (
                    <div>
                      <div
                        style={{
                          color: '#8b949e',
                          fontSize: 10,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: 600,
                          marginBottom: 8,
                        }}
                      >
                        Recent log lines
                      </div>
                      <pre
                        style={{
                          background: '#0d1117',
                          border: '1px solid #21262d',
                          borderRadius: 6,
                          padding: 12,
                          fontSize: 11,
                          fontFamily: 'monospace',
                          margin: 0,
                          overflow: 'auto',
                          maxHeight: 240,
                          lineHeight: 1.5,
                        }}
                      >
                        {job.recent_lines.map((line, i) => (
                          <span
                            key={i}
                            style={{
                              color: logLineColor(line),
                              display: 'block',
                            }}
                          >
                            {line}
                          </span>
                        ))}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filteredJobs.length === 0 && (
          <div
            style={{
              color: '#484f58',
              textAlign: 'center',
              padding: 40,
              fontSize: 13,
            }}
          >
            No jobs match &ldquo;{search}&rdquo;
          </div>
        )}
      </div>

      <p
        style={{
          color: '#484f58',
          fontSize: 11,
          marginTop: 20,
          textAlign: 'center',
        }}
      >
        Status derived from log file modification time. &quot;Healthy&quot; =
        within 1.5&times; interval. &quot;Stale&quot; = 1.5&ndash;2.5&times;.
        &quot;Overdue&quot; = &gt;2.5&times;.
      </p>
    </div>
  );
}
